<template>
    <div>
        <div v-if="!fetchingSpaceInvitations">
            <div class="d-flex flex-wrap align-center">
                <SpaceInviteDialog>
                    <template v-slot:activator>
                        <v-btn color="primary">
                            <v-icon left small>add</v-icon>
                            Invite
                        </v-btn>
                    </template>
                </SpaceInviteDialog>
                <v-spacer />
                <v-text-field
                    v-model="invitationEmailSearch"
                    autofocus
                    placeholder="Search for an invitation..."
                    prepend-inner-icon="mdi-magnify"
                    solo
                    flat
                    dense
                    hide-details
                    clearable>
                    <template v-slot:append>
                        <v-menu offset-y :close-on-content-click="false" :disabled="!spaceInvitations.length">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" small class="mr-n2" :disabled="!spaceInvitations.length">
                                    <v-icon left>mdi-tune-variant</v-icon>
                                    <span>Filters</span>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <div v-for="invitationType in availableInvitationTypes" :key="invitationType">
                                    <v-sheet rounded class="py-2 px-4 my-1">
                                        <v-switch inset dense hide-details class="pa-0 ma-0" v-model="invitationsSwitchData[invitationType]">
                                            <template v-slot:label>
                                                <div>
                                                    <v-icon left small :color="getMailIconColor(invitationType)">mdi-email-outline</v-icon>
                                                    <span>{{ capitalize(invitationAndRequestStatusTypes[invitationType]) }}</span>
                                                </div>
                                            </template>
                                        </v-switch>
                                    </v-sheet>
                                </div>
                            </v-list>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn class="ml-2" v-on="on" @click="$store.dispatch('spaceStore/fetchSpaceInvitations', $route.params.sid)" text icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>refresh</span>
                </v-tooltip>
            </div>

            <v-data-table
                :headers="invitationHeaders"
                :items="filteredInvitationsList"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                item-key="invid"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <v-icon :color="getMailIconColor(getInvitationStatus(item))">mdi-email-outline</v-icon>
                </template>
                <template v-slot:[`item.invitation`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div class="font-weight-medium secondary--text">{{ item.requestee_email }}</div>
                        <div class="caption mt-1" v-if="item.notification_timestamp">
                            <span class="font-weight-bold">Sent on:</span>
                            {{ item.notification_timestamp | dateTimeToHuman }}
                        </div>
                        <div class="caption mt-1" v-if="getInvitationStatus(item) === invitationAndRequestStatusTypes.REVOKED && item.revocation_timestamp">
                            <span class="font-weight-bold">Revoked on:</span>
                            {{ item.revocation_timestamp | dateTimeToHuman }}
                        </div>
                        <div
                            v-if="getInvitationStatus(item) === invitationAndRequestStatusTypes.ACCEPTED && invitationOrRequestHasTimestamp(item)"
                            class="caption mt-1">
                            <span class="font-weight-bold">Accepted on:</span>
                            {{ item.invitation_responses[0].timestamp | dateTimeToHuman }}
                        </div>
                        <div
                            v-else-if="getInvitationStatus(item) === invitationAndRequestStatusTypes.REJECTED && invitationOrRequestHasTimestamp(item)"
                            class="caption mt-1">
                            <span class="font-weight-bold">Rejected on:</span>
                            {{ item.invitation_responses[0].timestamp | dateTimeToHuman }}
                        </div>
                    </div>
                </template>
                <template v-slot:[`item.roles`]="{ item }">
                    <v-chip-group column>
                        <template v-for="(role, index) in item.roles">
                            <v-tooltip left :key="index" v-if="role.role_type !== invitationAndRequestRoleTypes.ORG_ROLE">
                                <template v-slot:activator="{ on }">
                                    <v-chip outlined small label v-on="on">
                                        <span v-if="role.role_type === invitationAndRequestRoleTypes.INSTANCE_ROLE" class="d-flex align-center">
                                            <v-icon small left v-if="role.Name === 'N/A'">mdi-help-circle-outline</v-icon>
                                            <v-icon v-else small left>{{ instanceRole(role.Name).icon }}</v-icon>
                                            <span v-if="role.Instance">{{ role.Instance }}</span>
                                            <span v-else>Target deleted</span>
                                        </span>
                                        <span v-else-if="role.role_type === invitationAndRequestRoleTypes.SPACE_ROLE" class="d-flex align-center">
                                            <v-icon v-if="role.Name === 'N/A'" small left>mdi-help-circle-outline</v-icon>
                                            <v-icon v-else small left>mdi-shield-account-outline</v-icon>
                                            <span v-if="role.Space">{{ role.Space }}</span>
                                            <span v-else>Target deleted</span>
                                        </span>
                                    </v-chip>
                                </template>
                                <span v-if="role.Name === 'N/A'">The target object has been deleted</span>
                                <span v-else-if="role.method === roleUpdateOptions.OVERWRITE_CURRENT_ROLE">
                                    This role will overwrite the current user role, even if the current role is more powerful that this one.
                                </span>
                                <span v-else-if="role.method === roleUpdateOptions.OVERWRITE_CURRENT_ROLE_IF_LOWER">
                                    This role will overwrite the current role only if the current role is less powerful than this one.
                                </span>
                            </v-tooltip>
                        </template>
                    </v-chip-group>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu
                        left
                        offset-x
                        nudge-top="10"
                        v-if="
                            getInvitationStatus(item) === invitationAndRequestStatusTypes.PENDING ||
                            getInvitationStatus(item) === invitationAndRequestStatusTypes.CREATED
                        ">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="
                                    () => {
                                        $store.dispatch('spaceStore/fetchSpaceInvitations', $route.params.sid)
                                    }
                                "
                                :invitationInfo="item"
                                :actionType="actionTypes.RESEND_INVITATION"></InvitationActionsDialog>
                            <copy-to-clipboard :textToCopy="item.link" buttonName="Copy invitation link" type="listItem"></copy-to-clipboard>
                            <InvitationActionsDialog
                                activatorType="listItem"
                                :successFetchAction="
                                    () => {
                                        $store.dispatch('spaceStore/fetchSpaceInvitations', $route.params.sid)
                                    }
                                "
                                :invitationInfo="item"
                                :actionType="actionTypes.REVOKE_INVITATION"></InvitationActionsDialog>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:no-data>
                    <span v-if="invitationEmailSearch">No invitations found.</span>
                    <span v-else>No invitations found. Consider inviting new users via the "+ Invite" button above.</span>
                </template>
            </v-data-table>
        </div>

        <div v-else-if="fetchingSpaceInvitations">
            <div
                v-for="item in [
                    { id: 'c1', opacity: 1 },
                    { id: 'c2', opacity: 0.75 },
                    { id: 'c3', opacity: 0.5 }
                ]"
                :key="item.id"
                class="my-6">
                <div :style="{ opacity: item.opacity }">
                    <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState } from 'vuex'
import { instanceRole, sortArray, capitalize } from '@/utils'
import { enumsData } from '@/mixins/enums'
import { invitationsAndRequests } from '@/mixins/invitationsAndRequests'
import SpaceInviteDialog from '@/modules/space/components/SpaceInviteDialog.vue'
const CopyToClipboard = () => import('@/components/CopyToClipboard')
const InvitationActionsDialog = () => import('@/modules/invitations/components/InvitationsActionsDialog')
export default {
    name: 'SpaceInvitations',
    mixins: [invitationsAndRequests, enumsData],
    components: {
        CopyToClipboard,
        InvitationActionsDialog,
        SpaceInviteDialog
    },
    methods: {
        capitalize,
        instanceRole
    },
    data() {
        return {
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            invitationsSwitchData: {
                ACCEPTED: true,
                PENDING: true,
                REJECTED: true,
                REVOKED: true,
                EXPIRED: true,
                BROKEN: true,
                CREATED: true
            },
            invitationEmailSearch: ''
        }
    },
    computed: {
        ...mapState('spaceStore', ['spaceInvitations', 'fetchingSpaceInvitations']),
        invitationHeaders() {
            return [
                { text: 'Type', value: 'type', sortable: false, width: '40px' },
                { text: 'Invitation', value: 'invitation', sortable: false },
                { text: 'Roles', value: 'roles', sortable: false },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '100px' }
            ]
        },
        availableInvitationTypes: function () {
            const invitationTypes = []
            if (this.spaceInvitations && this.spaceInvitations.length) {
                this.spaceInvitations.forEach(invitation => {
                    const invitationType = this.getInvitationStatus(invitation)
                    if (!invitationTypes.includes(invitationType)) {
                        invitationTypes.push(invitationType)
                    }
                })
                return invitationTypes
            }
            return []
        },
        displayedInvitationTypes: function () {
            const filteredInvitations = Object.values(this.invitationAndRequestStatusTypes).filter(
                value => this.availableInvitationTypes.includes(value) && this.invitationsSwitchData[value]
            )
            return filteredInvitations
        },
        filteredInvitationsList: function () {
            const filterByStatusType = this.spaceInvitations.filter(invitation => this.displayedInvitationTypes.includes(this.getInvitationStatus(invitation)))
            if (!this.invitationEmailSearch) {
                const sortedData = sortArray(filterByStatusType, 'invid', 'descending', false)
                return sortedData
            } else {
                const filteredData = filterByStatusType.filter(invitation =>
                    invitation.requestee_email.toLowerCase().includes(this.invitationEmailSearch.toLowerCase())
                )
                const sortedData = sortArray(filteredData, 'invid', 'descending', false)
                return sortedData
            }
        }
    },
    watch: {
        availableInvitationTypes: function (nextVal) {
            if (nextVal.length && nextVal.includes(this.invitationAndRequestStatusTypes.PENDING)) {
                this.invitationsSwitchData = {
                    ACCEPTED: false,
                    PENDING: true,
                    REJECTED: false,
                    REVOKED: false,
                    EXPIRED: false,
                    BROKEN: false,
                    CREATED: false
                }
            }
        }
    }
}
</script>
